import { Create } from '@material-ui/icons';
import { useEffect, useRef, useState } from 'react';
import {
  ResponseMessages,
  SupportCreateTicket,
} from '../../../types/commonTypes';
import { Modal, ModalContent } from '../../../components/Modal';
import { TicketContactInformation } from './TicketContactInformation';
import { SubjectSiteFields } from './SubjectSiteFields';
import { TicketCategories } from './TicketCategories';
import { TicketSubCategories } from './TicketSubCategories';
import { CreateTicketResponseBar } from './CreateTicketResponseBar';
import { useForm } from '../../../hooks/useForm';
import { useSupportTicket } from '../../../hooks/SupportTicket/useSupportTicket';
import { Loading } from '../../../components/Loading';

interface Props {
  onClose(): void;
  onCreateTicket(): void;
  open: boolean;
}

export const CreateTicketModal = ({ onClose, onCreateTicket, open }: Props) => {
  const [responseMessage, setResponseMessage] = useState<ResponseMessages>();
  const [ticketSubmitted, setTicketSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ResponseMessages>();
  const {
    categories,
    createTicket,
    loading: createLoading,
    error,
    response,
  } = useSupportTicket();
  const scrollRef = useRef<HTMLElement>();

  const { values, onChange, resetForm, updateField, onSubmit } =
    useForm<SupportCreateTicket>(
      {
        site: null,
        subject: '',
        category: null,
        subcategories: [],
        name: '',
        phone: '',
        cc: '',
        text: '',
        attachment: null,
      },
      () => {
        createTicket(values);
        setTicketSubmitted(true);
      }
    );

  const handleClose = () => {
    setErrorMessage(undefined);
    setResponseMessage(undefined);
    setTicketSubmitted(false);
    resetForm();
    onClose();
  };

  useEffect(() => {
    if (ticketSubmitted) {
      if (!createLoading && error === null) {
        setResponseMessage({
          message: 'Ticket Submitted Successfully',
          subMessage: `Your ticket number is: ${response.DisplayID}`,
        });
        onCreateTicket();
      } else {
        if (error) {
          setErrorMessage({
            message: 'Error Submitting Ticket',
            subMessage: '',
          });
        }
      }
    }
  }, [createLoading, response, error, ticketSubmitted]);

  return (
    <Modal
      id="create-ticket-modal"
      header="Create New Ticket"
      headerIcon={<Create />}
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      actionSuccess={!createLoading && Boolean(responseMessage)}
      actionError={!createLoading && Boolean(errorMessage)}
      actionMessages={{
        error: errorMessage ?? { message: '' },
        success: responseMessage ?? { message: '' },
      }}
      actionMessageTimeout={4000}
    >
      {createLoading ? (
        <>
          <div className="fixed inset-0 z-30">
            <Loading
              text={ticketSubmitted ? 'Creating Ticket...' : undefined}
            />
          </div>
          <div className="fixed bg-black/20 inset-0 z-20 backdrop-blur-[1px]" />
        </>
      ) : null}
      <ModalContent>
        <form className="flex flex-col w-full" onSubmit={onSubmit}>
          <div className="mt-[10px] max-h-[450px] p-2 overflow-auto">
            <TicketContactInformation
              ticketFields={values}
              loading={createLoading}
              onChange={onChange}
            />
            <SubjectSiteFields
              ticketFields={values}
              loading={createLoading}
              onChange={onChange}
              updateFormField={updateField}
            />
            <TicketCategories
              categories={categories}
              loading={createLoading}
              updateFormField={updateField}
            />
            <TicketSubCategories
              ticketFields={values}
              loading={createLoading}
              updateFormField={updateField}
              categories={categories}
              scrollRef={scrollRef}
            />
          </div>
          <CreateTicketResponseBar
            ticketFields={values}
            loading={createLoading}
            onChange={onChange}
            updateFormField={updateField}
            onClose={onClose}
          />
        </form>
      </ModalContent>
    </Modal>
  );
};
